import compareStrings from 'string-comparison'
import { WorkPeriodTag } from '../../../../api/work-period-tags'
import { CreateTagConfirmation, SimilarityResult, TagOption } from './types'
import { getWorkPeriodsInInterval } from '../../../../utils/missions'
import { getDocIdAsString } from '../../../../utils/documents'
import { Mission } from '../../../../api/missions'

/**
 * Check if a new tag name is too similar to existing tags
 * @param newTagName - The new tag name to check
 * @param availableTags - Array of existing tags
 * @param threshold - Similarity threshold (default: 0.8)
 * @returns SimilarityResult if similar tag found, null otherwise
 */
export const checkTagSimilarity = (
  newTagName: string,
  availableTags: WorkPeriodTag[],
  threshold: number = 0.8,
): SimilarityResult | null => {
  const normalizedNewTag = newTagName.toLowerCase().trim()

  for (const tag of availableTags) {
    const normalizedExistingTag = tag.name.toLowerCase().trim()
    const similarity = compareStrings.cosine.similarity(normalizedNewTag, normalizedExistingTag)

    if (similarity >= threshold) {
      return {
        similarTag: tag.name,
        similarity: Math.round(similarity * 100),
      }
    }
  }

  return null
}

/**
 * Convert work period tags to select options
 * @param tags - Array of work period tags
 * @returns Array of tag options for select component
 */
export const convertTagsToOptions = (tags: WorkPeriodTag[]): TagOption[] => {
  return tags.map(tag => ({
    value: tag._id,
    label: tag.name,
  }))
}

/**
 * Validate form values
 * @param values - Form values to validate
 * @returns Validation errors object
 */
export const validateFormValues = (): Record<string, string> => {
  const errors: Record<string, string> = {}
  // Add validation logic if needed
  return errors
}

/**
 * Handle tag creation with similarity check
 * @param inputValue - Input value for new tag
 * @param availableTags - Available tags to check against
 * @param onSimilarityFound - Callback when similarity is found
 * @param onConfirmationNeeded - Callback when confirmation is needed
 * @param onFocusOut - Callback to blur the input field
 */
export const handleTagCreation = (
  inputValue: string,
  availableTags: WorkPeriodTag[],
  onConfirmationNeeded: (confirmation: CreateTagConfirmation) => void,
  onFocusOut: () => void,
): void => {
  if (!inputValue.trim()) return

  const trimmedValue = inputValue.trim()
  const similarityCheck = checkTagSimilarity(trimmedValue, availableTags)

  // Focus out du champ
  onFocusOut()

  // Afficher une confirmation même si pas de similarité
  onConfirmationNeeded({
    newTag: trimmedValue,
    similarTagWarning: similarityCheck
      ? {
          newTag: trimmedValue,
          similarTag: similarityCheck.similarTag,
          similarity: similarityCheck.similarity,
        }
      : undefined,
  })
}

export const getUpdatableWorkPeriodsIds = (
  mission: Mission,
  startDate: Date,
  endDate: Date,
): string[] => {
  const workPeriodsInRange = getWorkPeriodsInInterval(mission, startDate, endDate)
  return workPeriodsInRange.filter(w => w.__actions.canBeUpdated).map(getDocIdAsString)
}
